import React, { useState } from 'react'
import { AnalysisSummary as AnalysisItem, updateAnalysisReview } from '../../../services/api'
import { STATUS } from './helpers'

export function ReviewModal({
  item, userEmail, teamName, onSave, onClose,
}: {
  item: AnalysisItem
  userEmail: string
  teamName?: string | null
  onSave: (newStatus: string, reviewedBy?: string | null) => void
  onClose: () => void
}) {
  const [reviewStatus, setReviewStatus] = useState(item.review_status)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  async function save() {
    setSaving(true)
    try {
      const updated = await updateAnalysisReview(item.id, reviewStatus, userEmail, teamName || undefined)
      onSave(updated.review_status, updated.reviewed_by)
      setSaved(true)
      setTimeout(() => { setSaved(false); onClose() }, 900)
    } finally {
      setSaving(false)
    }
  }

  function onBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      onClick={onBackdrop}
      className="fixed inset-0 z-[500] bg-black/35 backdrop-blur-[2px] flex items-center justify-center animate-[an-fadeIn_0.15s_ease]"
    >
      <div className="w-[400px] bg-an-surface border border-an-line-mid rounded-xl shadow-modal overflow-hidden animate-[an-in_0.18s_ease] font-geist">
        <div className="px-[18px] pt-4 pb-3.5 border-b border-an-line flex items-center gap-2.5">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-an-ink tracking-[-0.02em]">Review</div>
            <div className="text-[11px] text-an-ink-3 mt-px truncate">{item.title}</div>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-[5px] border border-an-line-mid bg-an-surface-2 text-an-ink-3 cursor-pointer text-sm flex items-center justify-center"
          >
            {'×'}
          </button>
        </div>

        <div className="px-[18px] py-4 flex flex-col gap-3">
          <div>
            <div className="text-[9px] font-bold text-an-ink-3 tracking-[0.09em] uppercase mb-[7px]">Status</div>
            <div className="flex gap-[5px] flex-wrap">
              {(['pending', 'accepted', 'rejected', 'dismissed', 'informational'] as const).map(s => {
                const st = STATUS[s]
                const active = reviewStatus === s
                return (
                  <button
                    key={s}
                    onClick={() => setReviewStatus(s)}
                    className="px-[11px] py-[5px] rounded-md text-xs cursor-pointer transition-all"
                    style={{
                      fontWeight: active ? 600 : 400,
                      border: `1px solid ${active ? st.c : 'var(--an-line-mid)'}`,
                      background: active ? st.bg : 'transparent',
                      color: active ? st.c : 'var(--an-ink-3)',
                    }}
                  >
                    {st.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="px-[18px] pb-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-[7px] border border-an-line-mid bg-transparent text-an-ink-2 text-[13px] font-medium cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-[2] py-2 rounded-[7px] border-none text-[13px] font-bold transition-all"
            style={{
              background: saved ? 'var(--an-green-bg)' : 'var(--an-accent)',
              color: saved ? 'var(--an-green)' : 'white',
              opacity: saving ? 0.7 : 1,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Review'}
          </button>
        </div>
      </div>
    </div>
  )
}
